/**
 * /wireline-slickline/rates — the Wireline & Slickline rate card (GET /wireline-slickline/rates,
 * fieldiq-api#1345, WL-4).
 *
 * Full width. Filters in the URL: the card (`card=default`, a customer's id,
 * or every card) and the kind. A rate reads in the company's currency format —
 * `CA$650.00/hr`; a third-party rate has no rate of its own and reads as
 * `At cost` or `Cost + 15%` — and an absent figure is `—`. New and edit are full pages
 * (RateDetailScreen), never a dialog.
 */
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Tags } from 'lucide-react';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { useAuth } from '../../../contexts/AuthContext';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { useWlCustomers, useWlMeta } from '../../../hooks/useWirelineSlickline';
import { useWlRates } from '../../../hooks/useWlTickets';
import { queryLoadFailure } from '../../../lib/load-failure';
import { canWriteWlTickets } from '../../../lib/wireline-slickline-tickets';
import { wlCatalogLabel } from '../../../lib/wireline-slickline';
import { wlDay } from '../../../lib/wireline-slickline-jobs';
import type { WlRate, WlTicketCatalog } from '../../../types/wireline-slickline-tickets';
import { DH_BAND_SECONDARY } from '../../downhole/DownholeBits';
import { useOpenRecord } from '../../downhole/recordPage';
import { WL_EYEBROW, WL_INPUT, WlChip, WlChipRow, WlEmpty, WlPanel, WlRegister, type WlColumn } from '../WlBits';
import { wlRatePriceText } from './TicketBits';

/**
 * What a rate prices beyond its kind: the crew position, the wireline unit,
 * the run kind, the depth or shot basis, how a third party is billed. Labels
 * are the API's; a value with no label reads as itself.
 */
export function wlRateAppliesTo(r: WlRate, catalog?: WlTicketCatalog | null) {
  const parts = [
    r.crewPosition ? r.crewPositionLabel || wlCatalogLabel(catalog?.crewPositions, r.crewPosition) : null,
    r.unitId ? (r.unitNumber ? `Unit ${r.unitNumber}` : 'One unit') : null,
    r.runKind ? r.runKindLabel || wlCatalogLabel(catalog?.runKinds, r.runKind) : null,
    r.depthBasis ? r.depthBasisLabel || wlCatalogLabel(catalog?.depthBases, r.depthBasis) : null,
    r.shotBasis ? r.shotBasisLabel || wlCatalogLabel(catalog?.shotBases, r.shotBasis) : null,
    r.kind === 'third_party' ? r.pricingLabel || wlCatalogLabel(catalog?.thirdPartyPricing, r.pricing) : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
}

export default function WlRatesScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const { user } = useAuth();
  const canWrite = canWriteWlTickets(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const card = searchParams.get('card') ?? '';
  const kind = searchParams.get('kind') ?? '';
  const setParam = (key: string, next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set(key, next);
    else p.delete(key);
    setSearchParams(p, { replace: true });
  };
  const catalog = (useWlMeta().data?.data ?? null) as WlTicketCatalog | null;
  const customers = useWlCustomers().data ?? [];
  const listQuery = useWlRates({ customer_id: card || undefined, kind: kind || undefined });
  const allQuery = useWlRates();
  const rates = listQuery.data?.data ?? [];
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (r: WlRate) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const today = allQuery.data?.meta.today ?? null;
  const live = (r: WlRate) => !!today && r.effectiveFrom <= today && (!r.effectiveTo || r.effectiveTo >= today);
  const filtered = !!(card || kind);

  const columns: WlColumn<WlRate>[] = [
    { key: 'code', header: 'Code', render: (r) => (<><p className="font-mono font-extrabold text-white">{r.code || '—'}</p><p className="mt-0.5 text-xs text-fieldiq-muted">{r.name || '—'}</p></>) },
    { key: 'card', header: 'Card', render: (r) => (r.customerId ? r.customerName || '—' : 'Default card') },
    { key: 'kind', header: 'Kind', render: (r) => r.kindLabel || wlCatalogLabel(catalog?.priceKinds, r.kind) },
    { key: 'applies', header: 'Applies to', render: (r) => wlRateAppliesTo(r, catalog) },
    { key: 'unit', header: 'Unit', render: (r) => r.unitLabel || wlCatalogLabel(catalog?.priceUnits, r.unit) },
    { key: 'rate', header: 'Rate', align: 'right', render: (r) => <span className="font-mono">{wlRatePriceText(r)}</span> },
    { key: 'currency', header: 'Currency', render: (r) => <span className="font-mono">{r.currency || '—'}</span> },
    { key: 'period', header: 'Effective', render: (r) => `${wlDay(r.effectiveFrom)} – ${r.effectiveTo ? wlDay(r.effectiveTo) : 'no end'}` },
  ];

  return (
    <div data-testid="wl-rates-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WL_EYEBROW}
        title="Rate Card"
        description="What the company bills — unit time, crew, depth, shots, guns, plugs, standby, travel, mileage, rent-ins, third-party and other charges — on the default card or a customer's own."
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          { icon: <Tags className="size-4" aria-hidden="true" />, label: 'Rates', tone: 'neutral', loading: allQuery.isLoading, value: countOf(() => true) },
          { icon: <Tags className="size-4" aria-hidden="true" />, label: 'In effect today', tone: 'accent', loading: allQuery.isLoading, value: countOf(live) },
          { icon: <Tags className="size-4" aria-hidden="true" />, label: 'Customer rates', tone: 'neutral', loading: allQuery.isLoading, value: countOf((r) => !!r.customerId) },
        ]}
        actions={
          canWrite ? (
            <button
              type="button"
              className={DH_BAND_SECONDARY}
              onClick={() => navigate(`/wireline-slickline/rates/new${card && card !== 'default' ? `?customerId=${card}` : ''}`)}
            >
              <Plus className="size-4" aria-hidden="true" />
              New rate
            </button>
          ) : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <WlChipRow label="Filter by kind">
            <WlChip label="All kinds" active={!kind} onClick={() => setParam('kind', '')} />
            {(catalog?.priceKinds ?? []).map((k) => (
              <WlChip key={k.value} label={k.label} active={kind === k.value} onClick={() => setParam('kind', k.value)} count={allQuery.isLoading ? undefined : countOf((r) => r.kind === k.value)} />
            ))}
          </WlChipRow>
          <div className="grid gap-3 sm:grid-cols-[16rem]">
            <select className={WL_INPUT} aria-label="Filter by card" value={card} onChange={(e) => setParam('card', e.target.value)}>
              <option value="">Every card</option>
              <option value="default">Default card</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <WlPanel eyebrow="Rate card" title="Rates" testId="wl-rate-register" footer={!listQuery.isLoading && rates.length ? `${rates.length} rate${rates.length === 1 ? '' : 's'}` : undefined}>
            <WlRegister
              query={listQuery}
              rows={rates}
              columns={columns}
              rail={(r) => (live(r) ? 'border-l-fi-green' : 'border-l-fi-steel')}
              onOpen={(r) => openRecord(`/wireline-slickline/rates/${r.id}`)}
              subject="rates"
              testId="wl-rate"
              minWidth="min-w-[1080px]"
              empty={filtered ? <WlEmpty>No rate matches these filters.</WlEmpty> : <WlEmpty>No rates yet. Add the first one to price field tickets.</WlEmpty>}
              card={(r) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-sm font-extrabold text-white">{r.code || '—'}</p>
                      <p className="mt-0.5 text-xs text-fieldiq-muted">{r.name || '—'} · {r.customerId ? r.customerName || '—' : 'Default card'}</p>
                    </div>
                    <span className="font-mono text-sm text-white">{wlRatePriceText(r)}</span>
                  </div>
                  <p className="mt-2 text-xs text-fieldiq-text">
                    {r.kindLabel || wlCatalogLabel(catalog?.priceKinds, r.kind)} · {wlRateAppliesTo(r, catalog)} · from {wlDay(r.effectiveFrom)}
                  </p>
                </>
              )}
            />
          </WlPanel>
        </div>
      </section>
    </div>
  );
}
